import type { I18n } from "@lingui/core"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Button, Combobox, TextInput, useCombobox } from "@mantine/core"
import { useEffect, useMemo } from "react"
import type { ICompanySpeaker } from "../../lib/api"
import { formatShortDate } from "../../lib/format-date"
import { identityInkColor, speakerColor } from "../../lib/speaker-colors"
import { speakerInitials } from "../../lib/speaker-format"
import type { IProps, TSpeakerRosterChoice } from "./interfaces"

import classes from "./styles.module.css"

const CREATE_PREFIX = "create:"
const PERSON_PREFIX = "person:"
const SOMEONE_NEW = "someone-new"

function personValue(id: string): string {
  return `${PERSON_PREFIX}${id}`
}

function createValue(name: string): string {
  return `${CREATE_PREFIX}${name}`
}

function hasExactMatch(people: readonly ICompanySpeaker[], query: string): boolean {
  const needle = query.trim().toLowerCase()
  if (needle.length === 0) return true
  return people.some((person) => person.name.toLowerCase() === needle)
}

/**
 * Suggested person first (when the query still matches), then roster hits in
 * API order, with that id removed so a known voice isn't listed twice.
 */
function visiblePeople(
  people: readonly ICompanySpeaker[],
  suggested: ICompanySpeaker | null,
  query: string,
): ICompanySpeaker[] {
  const needle = query.trim().toLowerCase()
  const roster = needle.length === 0 ? people : people.filter((person) => person.name.toLowerCase().includes(needle))
  if (!suggested) return [...roster]
  const suggestionMatches = needle.length === 0 || suggested.name.toLowerCase().includes(needle)
  if (!suggestionMatches) return [...roster]
  return [suggested, ...roster.filter((person) => person.id !== suggested.id)]
}

function personMeta(person: ICompanySpeaker, i18n: I18n): string {
  const meetings = person.recognized_meeting_count
  const lastSeen = person.last_seen_at ? formatShortDate(i18n, person.last_seen_at) : ""
  if (lastSeen.length > 0) {
    return meetings === 1
      ? i18n._(msg`last seen ${lastSeen} · in ${meetings} meeting`)
      : i18n._(msg`last seen ${lastSeen} · in ${meetings} meetings`)
  }
  return meetings === 1 ? i18n._(msg`in ${meetings} meeting`) : i18n._(msg`in ${meetings} meetings`)
}

export function SpeakerRosterPicker({
  query,
  onQueryChange,
  people,
  loading = false,
  suggested = null,
  onCommit,
  onCancel,
  using = false,
  error = null,
}: IProps): React.JSX.Element {
  const { i18n } = useLingui()
  const combobox = useCombobox({
    defaultOpened: true,
    onDropdownClose: () => combobox.resetSelectedOption(),
  })

  const trimmed = query.trim()
  const visible = useMemo(() => visiblePeople(people, suggested, trimmed), [people, suggested, trimmed])
  const showCreate = trimmed.length > 0 && !hasExactMatch(visible, trimmed)
  const showSomeoneNew = trimmed.length === 0

  const optionSignature = `${visible.map((person) => person.id).join("\u0000")}|${showCreate ? trimmed : ""}|${showSomeoneNew ? "1" : "0"}`
  useEffect(() => {
    // The signature is the trigger: options must be in the DOM before the first
    // one can be highlighted, and the effect body only calls into the combobox.
    if (optionSignature.length === 0) return
    combobox.selectFirstOption()
  }, [combobox, optionSignature])

  const commit = (choice: TSpeakerRosterChoice): void => {
    if (using || !onCommit) return
    onCommit(choice)
    combobox.closeDropdown()
  }

  const handleOptionSubmit = (value: string): void => {
    if (value === SOMEONE_NEW) return
    if (value.startsWith(PERSON_PREFIX)) {
      const id = value.slice(PERSON_PREFIX.length)
      const person = visible.find((item) => item.id === id) ?? people.find((item) => item.id === id)
      if (person) commit({ kind: "person", person })
      return
    }
    if (value.startsWith(CREATE_PREFIX)) {
      const name = value.slice(CREATE_PREFIX.length)
      if (name.length === 0) return
      commit({ kind: "create", name })
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    const code = event.nativeEvent.code
    const isEnter = code === "Enter" || code === "NumpadEnter" || event.key === "Enter"
    if (!isEnter || !combobox.dropdownOpened || using) return
    // Mantine only activates Enter when something is highlighted. Select the
    // first option first; Mantine's own handler then runs and clicks it.
    if (combobox.getSelectedOptionIndex() < 0) combobox.selectFirstOption()
  }

  return (
    <div className={classes.picker} data-testid="speaker-roster-picker">
      <div className={classes.fieldRow}>
        <div className={classes.field}>
          <Combobox store={combobox} onOptionSubmit={handleOptionSubmit} withinPortal={false}>
            <Combobox.Target>
              <TextInput
                value={query}
                onChange={(event) => {
                  onQueryChange(event.currentTarget.value)
                  combobox.openDropdown()
                }}
                onClick={() => combobox.openDropdown()}
                onFocus={() => combobox.openDropdown()}
                onKeyDown={handleKeyDown}
                placeholder={i18n._(msg`type a name or pick a person`)}
                autoComplete="off"
                autoFocus
                disabled={using}
                data-testid="speaker-roster-input"
              />
            </Combobox.Target>

            <Combobox.Dropdown>
              <Combobox.Options className={classes.options}>
                {loading && visible.length === 0 ? (
                  <Combobox.Empty>
                    <Trans>Looking up people…</Trans>
                  </Combobox.Empty>
                ) : null}
                {visible.map((person) => {
                  const fill = person.color ?? speakerColor(person.name)
                  const samples = person.sample_count
                  return (
                    <Combobox.Option className={classes.rosterOption} value={personValue(person.id)} key={person.id}>
                      <div className={classes.option}>
                        <span className={classes.mini} style={{ background: fill, color: identityInkColor(fill) }}>
                          {speakerInitials(person.name)}
                        </span>
                        <div>
                          <div className={classes.who}>{person.name}</div>
                          <div className={classes.sub}>{personMeta(person, i18n)}</div>
                        </div>
                        {samples > 0 ? (
                          <span className={`${classes.tag} ${classes.tagVoice}`}>
                            <Trans>known voice · {samples} samples</Trans>
                          </span>
                        ) : (
                          <span className={`${classes.tag} ${classes.tagNoVoice}`}>
                            <Trans>no voice yet</Trans>
                          </span>
                        )}
                      </div>
                    </Combobox.Option>
                  )
                })}
                {showCreate ? (
                  <Combobox.Option
                    className={classes.rosterOption}
                    value={createValue(trimmed)}
                    key={createValue(trimmed)}
                  >
                    <div className={classes.option}>
                      <span className={`${classes.mini} ${classes.miniCreate}`}>+</span>
                      <div>
                        <div className={classes.who}>
                          <Trans>Create "{trimmed}"</Trans>
                        </div>
                        <div className={classes.sub}>
                          <Trans>new person in this workspace</Trans>
                        </div>
                      </div>
                      <span className={`${classes.tag} ${classes.tagCreate}`}>
                        <Trans>new</Trans>
                      </span>
                    </div>
                  </Combobox.Option>
                ) : null}
                {showSomeoneNew ? (
                  <Combobox.Option className={classes.rosterOption} value={SOMEONE_NEW}>
                    <div className={classes.option}>
                      <span className={`${classes.mini} ${classes.miniCreate}`}>+</span>
                      <div>
                        <div className={classes.who}>
                          <Trans>Someone new…</Trans>
                        </div>
                        <div className={classes.sub}>
                          <Trans>Type their name</Trans>
                        </div>
                      </div>
                    </div>
                  </Combobox.Option>
                ) : null}
              </Combobox.Options>
            </Combobox.Dropdown>
          </Combobox>
        </div>
        <Button size="compact-sm" variant="subtle" onClick={onCancel} disabled={using}>
          <Trans>Cancel</Trans>
        </Button>
      </div>

      {error ? (
        <p className={classes.error} role="alert">
          {error}
        </p>
      ) : (
        <p className={classes.hint}>
          <Trans>
            Picking a person links this speaker to them and saves this voice as one of their samples. If they were
            linked to someone else, that link is removed.
          </Trans>
        </p>
      )}
    </div>
  )
}
